import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, FileText, Shield } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLegalSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kvkkUrl, setKvkkUrl] = useState("");
  const [cookiePolicyUrl, setCookiePolicyUrl] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/legal-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setKvkkUrl(data.kvkk_url || "");
      setCookiePolicyUrl(data.cookie_policy_url || "");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");

    if (!kvkkUrl.trim() || !cookiePolicyUrl.trim()) {
      toast.error("Tüm URL'leri doldurun");
      return;
    }

    // URL validation
    try {
      new URL(kvkkUrl);
      new URL(cookiePolicyUrl);
    } catch (error) {
      toast.error("Geçerli URL'ler girin (https:// ile başlamalı)");
      return;
    }

    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/legal-settings`,
        {
          kvkk_url: kvkkUrl,
          cookie_policy_url: cookiePolicyUrl,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Yasal metin linkleri güncellendi!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-teal-400">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-legal-settings">
      <h2 className="text-3xl font-black text-gradient">Yasal Metinler</h2>

      <div className="glass-card-premium rounded-2xl p-8 neon-border space-y-6">
        {/* Info */}
        <div className="bg-blue-500/10 border-2 border-blue-500/30 rounded-2xl p-5">
          <p className="text-blue-400 text-sm font-semibold mb-2">
            <FileText className="w-4 h-4 inline mr-2" />
            Yasal Metin Linkleri
          </p>
          <p className="text-gray-300 text-xs leading-relaxed">
            Bu linkler çerez banner'ında ve lead formunda gösterilir.<br />
            KVKK ve Çerez Politikası sayfalarınızın URL'lerini girin.
          </p>
        </div>

        {/* KVKK URL */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Shield className="w-4 h-4 inline mr-2" />
            KVKK Aydınlatma Metni URL
          </Label>
          <Input
            type="url"
            value={kvkkUrl}
            onChange={(e) => setKvkkUrl(e.target.value)}
            placeholder="https://alcodot.com/kvkk"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="kvkk-url-input"
          />
          <p className="text-xs text-gray-500">
            Lead formundaki KVKK checkbox'ı ve çerez banner'ındaki link bu URL'ye yönlendirecek
          </p>
        </div>

        {/* Cookie Policy URL */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <FileText className="w-4 h-4 inline mr-2" />
            Çerez Politikası URL
          </Label>
          <Input
            type="url"
            value={cookiePolicyUrl}
            onChange={(e) => setCookiePolicyUrl(e.target.value)}
            placeholder="https://alcodot.com/cerez-politikasi"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="cookie-policy-url-input"
          />
          <p className="text-xs text-gray-500">
            Çerez banner'ındaki "Çerez Politikası" linki bu URL'ye yönlendirecek
          </p>
        </div>

        {/* Preview */}
        <div className="bg-teal-500/10 border-2 border-teal-500/30 rounded-2xl p-6">
          <p className="text-xs text-teal-400 font-bold uppercase mb-3">Önizleme</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-teal-400" />
              <a
                href={kvkkUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-400 hover:text-teal-300 underline text-sm"
              >
                {kvkkUrl || "KVKK URL girilmedi"}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-400" />
              <a
                href={cookiePolicyUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-400 hover:text-teal-300 underline text-sm"
              >
                {cookiePolicyUrl || "Çerez Politikası URL girilmedi"}
              </a>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black text-lg shadow-lg neon-glow"
          data-testid="save-legal-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? "Kaydediliyor..." : "Yasal Metin Linklerini Kaydet"}
        </Button>
      </div>
    </div>
  );
};

export default AdminLegalSettings;
